
import { IConstructorOptions, NotifyPropertyChangedBase } from "../../../../Utilities";
import { ICacheableItem } from "../../BaseCache";
import { IDataModel, Models } from "./Models";
import { v4 as uuid } from 'uuid';





export type EntityKey = string;
export type EntityId = string;

export enum BaseProperties {
    PartitionKey = "PartitionKey", // This is the partition key for the Cosmos DB, must match
    id = "id", // This is the id for the Cosmos DB, must match
    typename = "_typeName",
    version = "_version",
    createdDate = '_createdDate',
    writeVersion = '_writeVersion',
}

export const ForcePropertyChangedNotification = "ForcePropertyChangedNotification";

export interface IDataEntityOptions {
    typename?: Models;
    serializationProperties?: any;
    jsonProperties?: string[];
    dateProperties?: string[];
    entityJSON?: string;
}





export class DataEntityBase extends NotifyPropertyChangedBase implements IDataModel, ICacheableItem {



    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region Static methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    public static OptionsFromId = (id: EntityId) => {
        var options: IConstructorOptions = { sourceObject: {} };
        options.sourceObject.id = id;
        return options;
    }

    public static ConstructorOptionsBuilder = (options?: IConstructorOptions) => {
        var baseOptions: IConstructorOptions = options ?? { sourceObject: {} };

        var addProp = (prop: string, value: any) => {
            baseOptions.sourceObject[prop] = value;
            return { addProp: addProp, construct: construct };
        }

        var addId = (id: EntityId) => {
            baseOptions.sourceObject.id = id;
            return { addProp: addProp, construct: construct };
        }

        var construct = () => baseOptions;

        return { addProp: addProp, addId: addId, construct: construct };
    }

    public static CreateId(typeName: string, key: EntityKey) {
        return `${typeName} | ${key}` as EntityId;
    }

    public static KeyFromId(id: EntityId) {
        return id.split('|')[1].trim() as EntityKey;
    }
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion Static methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////






    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region Public
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    
    public get writeVersion(): undefined | number { return this[BaseProperties.writeVersion]; }
    public set writeVersion(value: undefined | number) { this.UpdateAndFire(BaseProperties.writeVersion, value); }
    [BaseProperties.writeVersion] = undefined;

    public get _PartitionKey(): string | undefined { return this.PartitionKey; }
    public set _PartitionKey(newKey: string) { this.PartitionKey = newKey; }
    [BaseProperties.PartitionKey]: string | undefined = undefined

    public get typeName(): Models { return this._typeName; }
    [BaseProperties.typename] = Models.Base;


    public get version(): number { return this[BaseProperties.version]; }
    [BaseProperties.version] = -1;

    public get descriptor() {

        if (!this.key)
            throw new Error("Cannot create a descriptor for an object that has not been assigned an id.");
        return { typeName: this.typeName, id: this.key };
    }

    public get key() {
        return DataEntityBase.KeyFromId(this.id);
    }
    public set key(value: EntityKey) {
        this.id = DataEntityBase.CreateId(this.typeName, value);
    }
    public id: EntityId = '|';




    public get createdDate(): Date { return this[BaseProperties.createdDate]; }
    public set createdDate(value: Date) { this.UpdateAndFire(BaseProperties.createdDate, value); }
    [BaseProperties.createdDate] = new Date();


    /*************************************************************************/
    //
    // Public Methods
    //
    /*************************************************************************/
    public IdentifyDifferences(other: DataEntityBase) {
        let differences: string[] = [];
        
        let serializeProperties: string[] = this._serializeProperties;
        let defaultProperties = this.EnumToStringArray(BaseProperties);
        serializeProperties = serializeProperties.concat(defaultProperties);

        const myTemp: any = this;
        const otherTemp: any = other;   

        for (let prop of serializeProperties) {
            if (myTemp[prop] !== otherTemp[prop])
                differences.push(prop);
        }
        return differences;
    }

    public Serialize(preparedObject?: DataEntityBase) {

        const tempobj: any = preparedObject ? preparedObject : this;

        let serializeProperties: string[] = this._serializeProperties;

        let defaultProperties = this.EnumToStringArray(BaseProperties);
        serializeProperties = serializeProperties.concat(defaultProperties);

        let retVal: any = {};
        for (var prop of serializeProperties) {
            if (this._jsonProperties.includes(prop))
                retVal[prop] = JSON.stringify(tempobj[prop]);
            else
                retVal[prop] = tempobj[prop];
        }

        return retVal;
    }

    public ForcePropertyChangedNotification() {
        this.FireChanged(ForcePropertyChangedNotification);
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion 
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////






    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region Protected Methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    protected constructor(baseOptions: IDataEntityOptions & IConstructorOptions) {
        super();

        if (baseOptions.entityJSON && baseOptions.typename) {
            throw new Error("Cannot specify both entityJSON and a TypeName when constructing a DataEntityBase");
        }

        this._serializeProperties = baseOptions.serializationProperties ? this.EnumToStringArray(baseOptions.serializationProperties) : [];

        if (baseOptions.typename)
            this[BaseProperties.typename] = baseOptions.typename;

        if (baseOptions.dateProperties)
            this._dateProperties = [...baseOptions.dateProperties, BaseProperties.createdDate];

        if (baseOptions.jsonProperties)
            this._jsonProperties = baseOptions.jsonProperties;

    }

    protected CompleteConstruction(options?: IConstructorOptions) {
        this.ValidateConstructorOptions(options);
        if (options?.entityJSON)
            this.DeserializeHelper(options.entityJSON);

        if (options?.sourceObject) {
            //Utils.Logger.Describe(`creating from:\n${Utils.Logger.Stringify(options.sourceObject)}`);
            this.CopyFromJSONObject(options.sourceObject);
        }

        if (this.id === '|' || !this.id)
            this.key = uuid();
        else if (this.id && this.id.indexOf('|') === -1)
            throw new Error(`Invalid ID format. Must be in the format 'typeName | key' instead recieved '${this.id}'`);

    }

    protected ValidateConstructorOptions(options?: IConstructorOptions) {
        if (options && options.entityJSON && options.sourceObject)
            throw new Error("Cannot specify both entityJSON and sourceObject when constructing a DataEntityBase.");
    }

    protected FixDates(sourceObj: object) {
        for (var dateItem of this._dateProperties) {
            let t = sourceObj as any;
            if (t[dateItem])
                t[dateItem] = new Date(t[dateItem]);
        }
        return sourceObj;
    }

    protected DeserializeHelper(entityJSON: string) {
        let sourceObj = JSON.parse(entityJSON);
        return this.CopyFromJSONObject(sourceObj);
    }

    protected CopyFromJSONObject(source: object) {

        let tempObject: any = Object.assign({}, source);
        for (let prop of this._jsonProperties) {
            if (tempObject[prop]) {
                tempObject[prop] = JSON.parse(tempObject[prop]);
            }
        }

        this.FixDates(tempObject)
        for (var prop in tempObject) {
            if (this.hasOwnProperty(prop)) {
                (this as any)[prop] = tempObject[prop];
            }
        }
    }

    protected EnumToStringArray(enumInput: any) {
        return Object.keys(enumInput).map(key => enumInput[key]);
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion Protected Methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




    /***************************************************************************************************/
    //
    // Private Variables
    //
    /***************************************************************************************************/
    private _serializeProperties: string[] = [];
    private _dateProperties: string[] = [];
    private _jsonProperties: string[] = [];
}
